import { getOnlyObjectUniformsDeclaration } from '../main/uniforms/ObjectUniforms';
import { CameraUniforms } from '../main/CameraUniforms';
import { $wgsl } from '../../wgsl-preprocessor/wgsl-preprocessor';
import cullInstancesCode from './cull-instances.wgsl';
import cullDrawCallsCode from './cull-draw-calls.wgsl';
import visibilityCullingCode from './visibility-culling.wgsl';
import { UsingMultiDraw } from '../globals';

/** @import { Renderer } from "../Renderer" */
/** @import { MultiDrawBatch } from "./MultiDrawBatch" */

const WorkGroupSize = 64;

export class GPUCullingEngine {

  /** @type {GPUDevice} */
  #device;
  /** @type {CameraUniforms} */
  #cameraUniforms;

  /** @type {GPUBindGroupLayout} */
  #cameraBgLayout;
  /** @type {GPUBindGroupLayout} */
  #instanceBgLayout;
  /** @type {GPUComputePipeline} */
  #instancePipeline;

  /** @type {GPUBindGroupLayout} */
  #drawCallBgLayout;
  /** @type {GPUComputePipeline} */
  #drawCallPipeline;

  /** @type {GPUBindGroup} */
  #cameraBg;
  /** @type {boolean} */
  #cameraPaused = false;
  /** @type {GPUBindGroup|null} */
  #pausedCameraBg = null;
  /** @type {GPUBuffer|null} */
  #pausedCameraBuffer = null;

  /**
   * @param {Renderer} renderer
   */
  constructor(renderer) {
    this.#device = renderer.getDevice();
    this.#cameraUniforms = renderer.getMainPass().getCameraUniforms();

    this.initInstancePipeline();
    this.initDrawCallPipeline();

    this.#cameraBg = this.#createCameraBindGroup(this.#cameraUniforms.getBuffer());
  }

  initInstancePipeline() {
    this.#cameraBgLayout = this.#device.createBindGroupLayout({
      entries: [
      {
        binding: 0, // cameraUniforms
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'uniform' }
      }]

    });
    this.#instanceBgLayout = this.#device.createBindGroupLayout({
      entries: [
      {
        binding: 0, // objectUniforms
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'read-only-storage' }
      },
      {
        binding: 1, // cullUniforms
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'uniform' }
      },
      {
        binding: 2, // inDrawCalls
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'read-only-storage' }
      },
      {
        binding: 3, // instanceToDrawCall
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'read-only-storage' }
      },
      {
        binding: 4, // visibleInstanceCount
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'storage' }
      },
      {
        binding: 5, // visibleInstanceIds
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'storage' }
      }]

    });

    const bindGroupLayouts = [this.#cameraBgLayout, this.#instanceBgLayout];
    const pipelineLayout = this.#device.createPipelineLayout({ bindGroupLayouts });
    this.#instancePipeline = this.#device.createComputePipeline({
      label: 'cull instances pipeline',
      compute: {
        module: this.#device.createShaderModule({
          label: 'cull instances shader',
          code: $wgsl(cullInstancesCode, {
            cameraUniforms: CameraUniforms.getDeclaration(0, 0),
            objectUniforms: getOnlyObjectUniformsDeclaration(1),
            visibilityCulling: visibilityCullingCode
          })
        }),
        entryPoint: 'main',
        constants: { WorkGroupSize }
      },
      bindGroupLayouts,
      layout: pipelineLayout
    });
  }

  /**
   * @param {GPUBuffer} cameraUniformsBuffer
   * @returns {GPUBindGroup}
   */
  #createCameraBindGroup(cameraUniformsBuffer) {
    return this.#device.createBindGroup({
      layout: this.#cameraBgLayout,
      entries: [
      { binding: 0, resource: { buffer: cameraUniformsBuffer } }]

    });
  }

  /**
   * @param {MultiDrawBatch} mdBatch
   * @param {GPUBuffer} objectUniformsBuffer
   * @returns {GPUBindGroup}
   */
  createInstanceBindGroup(mdBatch, objectUniformsBuffer) {
    return this.#device.createBindGroup({
      layout: this.#instanceBgLayout,
      entries: [
      { binding: 0, resource: { buffer: objectUniformsBuffer } },
      { binding: 1, resource: { buffer: mdBatch.cullUniforms.getBuffer() } },
      { binding: 2, resource: { buffer: mdBatch.drawCallsBuffer } },
      { binding: 3, resource: { buffer: mdBatch.instanceToDrawCallBuffer } },
      { binding: 4, resource: { buffer: mdBatch.visibleInstanceCountBuffer } },
      { binding: 5, resource: { buffer: mdBatch.visibleInstanceIdsBuffer } }]

    });
  }

  initDrawCallPipeline() {
    const bgLayoutDesc = {
      entries: [
      {
        binding: 0, // cullUniforms
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'uniform' }
      },
      {
        binding: 1, // inDrawCalls
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'read-only-storage' }
      },
      {
        binding: 2, // edgeData
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'read-only-storage' }
      },
      {
        binding: 3, // visibleInstanceCount
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'read-only-storage' }
      },
      {
        binding: 6, // outDrawCalls
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'storage' }
      },
      {
        binding: 7, // outDrawCallsEdges
        visibility: GPUShaderStage.COMPUTE,
        buffer: { type: 'storage' }
      }]

    };
    if (UsingMultiDraw()) {
      bgLayoutDesc.entries.push(
        {
          binding: 4, // drawCallToMultiDrawCall
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: 'read-only-storage' }
        },
        {
          binding: 5, // rangeStartsPerMultiDraw
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: 'read-only-storage' }
        },
        {
          binding: 8, // outDrawCallCounts
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: 'storage' }
        });
    }
    this.#drawCallBgLayout = this.#device.createBindGroupLayout(bgLayoutDesc);

    const bindGroupLayouts = [this.#drawCallBgLayout];
    const pipelineLayout = this.#device.createPipelineLayout({ bindGroupLayouts });
    this.#drawCallPipeline = this.#device.createComputePipeline({
      label: 'cull draw calls pipeline',
      compute: {
        module: this.#device.createShaderModule({
          label: 'cull draw calls shader',
          code: $wgsl(cullDrawCallsCode, {
            usingMultiDraw: UsingMultiDraw()
          })
        }),
        entryPoint: 'main',
        constants: { WorkGroupSize }
      },
      bindGroupLayouts,
      layout: pipelineLayout
    });
  }

  /**
   * @param {MultiDrawBatch} mdBatch
   */
  createDrawCallBindGroup(mdBatch) {
    const bgDesc = {
      layout: this.#drawCallBgLayout,
      entries: [
      { binding: 0, resource: { buffer: mdBatch.cullUniforms.getBuffer() } },
      { binding: 1, resource: { buffer: mdBatch.drawCallsBuffer } },
      { binding: 2, resource: { buffer: mdBatch.edgeDataBuffer } },
      { binding: 3, resource: { buffer: mdBatch.visibleInstanceCountBuffer } },
      { binding: 6, resource: {
          buffer: mdBatch.drawCallsBlock.buffer,
          offset: mdBatch.drawCallsBlock.offset,
          size: mdBatch.drawCallsBlock.size
        } },
      { binding: 7, resource: {
          buffer: mdBatch.edgeDrawCallsBlock.buffer,
          offset: mdBatch.edgeDrawCallsBlock.offset,
          size: mdBatch.edgeDrawCallsBlock.size
        } }]

    };
    if (UsingMultiDraw()) {
      bgDesc.entries.push(
        { binding: 4, resource: { buffer: mdBatch.drawCallToMultiDrawCallBuffer } },
        { binding: 5, resource: { buffer: mdBatch.rangeStartsPerMultiDrawBuffer } },
        { binding: 8, resource: { buffer: mdBatch.drawCallCountsBuffer } }
      );
    }
    return this.#device.createBindGroup(bgDesc);
  }

  /**
   * @param {GPUCommandEncoder} cmd
   * @param {MultiDrawBatch} mdBatch
   */
  cull(cmd, mdBatch) {
    cmd.clearBuffer(mdBatch.visibleInstanceCountBuffer);
    if (UsingMultiDraw()) {
      cmd.clearBuffer(mdBatch.drawCallCountsBuffer);
    }

    const pass = cmd.beginComputePass({ label: 'culling compute pass' });

    pass.setPipeline(this.#instancePipeline);
    pass.setBindGroup(0, this.#cameraPaused ? this.#pausedCameraBg : this.#cameraBg);
    pass.setBindGroup(1, mdBatch.instanceBg);
    const instanceWorkgroups = Math.ceil(mdBatch.instanceCount / WorkGroupSize);
    pass.dispatchWorkgroups(instanceWorkgroups);

    pass.setPipeline(this.#drawCallPipeline);
    pass.setBindGroup(0, mdBatch.drawCallBg);
    const drawCallWorkgroups = Math.ceil(mdBatch.drawCallCount / WorkGroupSize);
    pass.dispatchWorkgroups(drawCallWorkgroups);

    pass.end();

  }

  /**
   * Creates a buffer to store a copy of camera uniforms and a bindgroup
   * pointing to it.
   */
  #createPauseCameraBuffer() {
    this.#pausedCameraBuffer = this.#device.createBuffer({
      label: 'paused camera buffer',
      size: this.#cameraUniforms.getBuffer().size,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });
    this.#pausedCameraBg = this.#createCameraBindGroup(this.#pausedCameraBuffer);
  }

  /**
   * Toggles camera pause on/off.
   * If toggled on, grabs a snapshot of the current camera buffer to be used
   * for culling until pause is toggled off.
   * @returns {boolean} The new paused state
   */
  toggleCameraPause() {
    this.#cameraPaused = !this.#cameraPaused;

    if (this.#cameraPaused) {
      if (!this.#pausedCameraBuffer) {
        this.#createPauseCameraBuffer();
      }

      const cameraCpuBuffer = this.#cameraUniforms.getCpuBuffer();
      this.#device.queue.writeBuffer(this.#pausedCameraBuffer, 0, cameraCpuBuffer);
    }
    return this.#cameraPaused;
  }

}